import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styled from "styled-components";
import Category from "./Category";
import type { CategoryProps } from "./Category";

interface Props extends CategoryProps {
  selectedValue: string;
  onClick: (name: string, flow: string) => void;
  showIcon?: boolean;
  isEditMode: boolean;
  isHidden: boolean;
  onToggleHidden: (name: string) => void;
}

const SortableWrapper = styled.div<{ $isDragging: boolean; $isHidden: boolean; $isEditMode: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  opacity: ${(props) =>
    props.$isDragging ? 0.5 : props.$isHidden ? 0.4 : 1};
  transition: opacity 0.2s ease;
  cursor: ${(props) => (props.$isEditMode ? "grab" : "pointer")};
  touch-action: ${(props) => (props.$isEditMode ? "none" : "auto")};

  &:active {
    cursor: ${(props) => (props.$isEditMode ? "grabbing" : "pointer")};
  }
`;

const CategoryFill = styled.div`
  flex: 1;
  display: flex;

  > button {
    flex: 1;
    width: 100%;
  }
`;

const ToggleButton = styled.button<{ $isHidden: boolean }>`
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 2;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.$isHidden ? "var(--interactive-primary)" : "var(--interactive-primary)")};
  background: ${(props) => (props.$isHidden ? "white" : "var(--interactive-primary)")};
  color: ${(props) => (props.$isHidden ? "var(--interactive-primary)" : "white")};
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  opacity: 1;

  &:hover {
    transform: scale(1.1);
  }
`;

const SortableCategory = ({
  image,
  name,
  flow,
  selectedValue,
  onClick,
  showIcon,
  isEditMode,
  isHidden,
  onToggleHidden,
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = (clickedName: string, clickedFlow: string) => {
    if (!isEditMode) {
      onClick(clickedName, clickedFlow);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleHidden(name);
  };

  return (
    <SortableWrapper
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      $isHidden={isHidden}
      $isEditMode={isEditMode}
      {...attributes}
      {...listeners}
    >
      {isEditMode && (
        <ToggleButton
          $isHidden={isHidden}
          onClick={handleToggle}
          aria-label={isHidden ? `Show ${name}` : `Hide ${name}`}
        >
          {isHidden ? "+" : "\u2212"}
        </ToggleButton>
      )}
      <CategoryFill>
        <Category
          image={image}
          name={name}
          flow={flow}
          selectedValue={selectedValue}
          onClick={handleClick}
          showIcon={showIcon}
        />
      </CategoryFill>
    </SortableWrapper>
  );
};

export default SortableCategory;